import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Article,
  Delete,
  Edit,
  ExpandLess,
  ExpandMore,
  Search,
} from "@mui/icons-material";
import {
  useGetSheqSopsQuery,
  useCreateSheqSopMutation,
  useUpdateSheqSopMutation,
  useDeleteSheqSopMutation,
  SheqSop,
  SopCategory,
  SopStatus,
} from "../../../redux/sheqApi";
import { useGetComsBlocksQuery } from "../../../redux/comsApi";

const NAVY = "#123456";

const SOP_CATEGORIES: { value: SopCategory; label: string }[] = [
  { value: "GENERAL", label: "General" },
  { value: "SAFETY", label: "Safety" },
  { value: "CONSTRUCTION", label: "Construction" },
  { value: "ELECTRICAL", label: "Electrical" },
  { value: "PLUMBING", label: "Plumbing" },
  { value: "SCAFFOLDING", label: "Scaffolding" },
  { value: "LIFTING", label: "Lifting" },
  { value: "EXCAVATION", label: "Excavation" },
  { value: "FINISHING", label: "Finishing" },
  { value: "FIRE", label: "Fire" },
  { value: "ENVIRONMENT", label: "Environment" },
];

const SOP_STATUSES: { value: SopStatus; label: string; color: string }[] = [
  { value: "DRAFT", label: "Draft", color: "#9e9e9e" },
  { value: "ACTIVE", label: "Active", color: "#2e7d32" },
  { value: "SUPERSEDED", label: "Superseded", color: "#f57c00" },
  { value: "ARCHIVED", label: "Archived", color: "#616161" },
];

function today() {
  return new Date().toISOString().slice(0, 10);
}

function reviewDateFromEffective(effectiveDate: string): string {
  const d = new Date(`${effectiveDate}T00:00:00`);
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

const EMPTY_SOP: Omit<SheqSop, "id" | "createdAt" | "updatedAt"> = {
  sopNumber: "",
  title: "",
  category: "GENERAL",
  revision: "Rev 0",
  effectiveDate: today(),
  reviewDate: reviewDateFromEffective(today()),
  status: "DRAFT",
  purpose: "",
  scope: "",
  procedure: "",
  hazards: "",
  requiredPpe: "",
  legislativeRef: "",
  blockId: "",
  blockName: "",
  approvedByName: "",
  notes: "",
};

// ─── SOP Card ───────────────────────────────────────────────────────────────
interface SopCardProps {
  sop: SheqSop;
  onEdit: (s: SheqSop) => void;
  onDelete: (id: string) => void;
}

const SopCard: React.FC<SopCardProps> = ({ sop, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const statusMeta =
    SOP_STATUSES.find((s) => s.value === sop.status) ?? SOP_STATUSES[0];
  const catLabel =
    SOP_CATEGORIES.find((c) => c.value === sop.category)?.label ?? sop.category;

  const isReviewDue = sop.reviewDate
    ? new Date(`${sop.reviewDate}T00:00:00`) <= new Date()
    : false;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        borderLeft: `4px solid ${statusMeta.color}`,
        "&:hover": { boxShadow: 2 },
      }}
    >
      {/* Header row */}
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        px={2}
        py={1.5}
        sx={{ cursor: "pointer" }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Article sx={{ color: NAVY, fontSize: 22, flexShrink: 0 }} />
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={700}>
              {sop.sopNumber}
            </Typography>
            <Typography variant="body2" noWrap>
              {sop.title}
            </Typography>
          </Box>
          <Box display="flex" gap={1} mt={0.25} flexWrap="wrap">
            <Chip
              label={statusMeta.label}
              size="small"
              sx={{
                bgcolor: statusMeta.color,
                color: "#fff",
                fontSize: 10,
                height: 18,
              }}
            />
            <Chip
              label={catLabel}
              size="small"
              sx={{ fontSize: 10, height: 18 }}
            />
            <Chip
              label={sop.revision}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10, height: 18 }}
            />
            {sop.blockName && (
              <Chip
                label={sop.blockName}
                size="small"
                variant="outlined"
                sx={{ fontSize: 10, height: 18 }}
              />
            )}
            {isReviewDue && sop.status === "ACTIVE" && (
              <Chip
                label="Review overdue"
                size="small"
                sx={{
                  bgcolor: "#c62828",
                  color: "#fff",
                  fontSize: 10,
                  height: 18,
                }}
              />
            )}
          </Box>
        </Box>
        <Stack direction="row" gap={0.5} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(sop)}>
              <Edit sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => sop.id && onDelete(sop.id)}
            >
              <Delete sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <IconButton size="small">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {/* Expanded detail */}
      <Collapse in={expanded}>
        <Divider />
        <Box px={2.5} py={2} display="flex" flexDirection="column" gap={1.5}>
          <Box display="flex" gap={4} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Effective Date
              </Typography>
              <Typography variant="body2">{sop.effectiveDate}</Typography>
            </Box>
            {sop.reviewDate && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Review Date
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: isReviewDue ? "#c62828" : "inherit" }}
                >
                  {sop.reviewDate}
                </Typography>
              </Box>
            )}
            {sop.approvedByName && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Approved By
                </Typography>
                <Typography variant="body2">{sop.approvedByName}</Typography>
              </Box>
            )}
          </Box>

          {sop.purpose && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
              >
                Purpose
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", mt: 0.25 }}
              >
                {sop.purpose}
              </Typography>
            </Box>
          )}

          {sop.scope && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
              >
                Scope
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", mt: 0.25 }}
              >
                {sop.scope}
              </Typography>
            </Box>
          )}

          {sop.procedure && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
              >
                Procedure
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", mt: 0.25 }}
              >
                {sop.procedure}
              </Typography>
            </Box>
          )}

          {sop.hazards && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
              >
                Hazards
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", mt: 0.25 }}
              >
                {sop.hazards}
              </Typography>
            </Box>
          )}

          {sop.requiredPpe && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
              >
                Required PPE
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {sop.requiredPpe}
              </Typography>
            </Box>
          )}

          {sop.legislativeRef && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
              >
                Legislative Reference
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {sop.legislativeRef}
              </Typography>
            </Box>
          )}

          {sop.notes && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
              >
                Notes
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {sop.notes}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};

// ─── SOP Form Dialog ────────────────────────────────────────────────────────
interface SopFormProps {
  open: boolean;
  initial: Partial<SheqSop>;
  onClose: () => void;
  onSave: (data: Omit<SheqSop, "id" | "createdAt" | "updatedAt">) => void;
  saving: boolean;
  blocks: { id: string; name: string }[];
}

const SopFormDialog: React.FC<SopFormProps> = ({
  open,
  initial,
  onClose,
  onSave,
  saving,
  blocks,
}) => {
  const [form, setForm] = useState<
    Omit<SheqSop, "id" | "createdAt" | "updatedAt">
  >({ ...EMPTY_SOP, ...initial });

  React.useEffect(() => {
    setForm({ ...EMPTY_SOP, ...initial });
  }, [initial, open]);

  const set =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleBlockChange = (blockId: string) => {
    const block = blocks.find((b) => b.id === blockId);
    setForm((f) => ({
      ...f,
      blockId,
      blockName: block?.name ?? "",
    }));
  };

  const handleEffectiveDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const effectiveDate = e.target.value;
    setForm((f) => ({
      ...f,
      effectiveDate,
      reviewDate: reviewDateFromEffective(effectiveDate),
    }));
  };

  const canSave =
    form.sopNumber.trim() && form.title.trim() && form.effectiveDate;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: NAVY, color: "#fff" }}>
        {initial.id ? "Edit SOP" : "New SOP"}
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {/* Row 1 */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="SOP Number *"
              value={form.sopNumber}
              onChange={set("sopNumber")}
              size="small"
              sx={{ flex: "1 1 160px" }}
            />
            <TextField
              label="Title *"
              value={form.title}
              onChange={set("title")}
              size="small"
              sx={{ flex: "3 1 300px" }}
            />
          </Box>

          {/* Row 2 */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <FormControl size="small" sx={{ flex: "1 1 160px" }}>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={form.category}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    category: e.target.value as SopCategory,
                  }))
                }
              >
                {SOP_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: "1 1 140px" }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={form.status}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    status: e.target.value as SopStatus,
                  }))
                }
              >
                {SOP_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Revision"
              value={form.revision}
              onChange={set("revision")}
              size="small"
              sx={{ flex: "1 1 100px" }}
            />
            <FormControl size="small" sx={{ flex: "1 1 180px" }}>
              <InputLabel>Site / Block</InputLabel>
              <Select
                label="Site / Block"
                value={form.blockId ?? ""}
                onChange={(e) => handleBlockChange(e.target.value as string)}
              >
                <MenuItem value="">All sites</MenuItem>
                {blocks.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Row 3 */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Effective Date *"
              type="date"
              value={form.effectiveDate}
              onChange={handleEffectiveDateChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ flex: "1 1 160px" }}
            />
            <TextField
              label="Review Date"
              type="date"
              value={form.reviewDate ?? ""}
              onChange={set("reviewDate")}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ flex: "1 1 160px" }}
            />
            <TextField
              label="Approved By"
              value={form.approvedByName ?? ""}
              onChange={set("approvedByName")}
              size="small"
              sx={{ flex: "2 1 200px" }}
            />
          </Box>

          <Divider />

          {/* Content fields */}
          <TextField
            label="Purpose"
            multiline
            rows={2}
            value={form.purpose ?? ""}
            onChange={set("purpose")}
            size="small"
            fullWidth
          />
          <TextField
            label="Scope"
            multiline
            rows={2}
            value={form.scope ?? ""}
            onChange={set("scope")}
            size="small"
            fullWidth
          />
          <TextField
            label="Procedure (step-by-step)"
            multiline
            rows={5}
            value={form.procedure ?? ""}
            onChange={set("procedure")}
            size="small"
            fullWidth
            placeholder="1. Put on PPE&#10;2. Inspect the work area&#10;3. ..."
          />
          <TextField
            label="Hazards"
            multiline
            rows={2}
            value={form.hazards ?? ""}
            onChange={set("hazards")}
            size="small"
            fullWidth
          />
          <TextField
            label="Required PPE (comma-separated codes)"
            value={form.requiredPpe ?? ""}
            onChange={set("requiredPpe")}
            size="small"
            fullWidth
            placeholder="HH, SS, GL, DM"
          />
          <TextField
            label="Legislative Reference"
            value={form.legislativeRef ?? ""}
            onChange={set("legislativeRef")}
            size="small"
            fullWidth
          />
          <TextField
            label="Notes"
            multiline
            rows={2}
            value={form.notes ?? ""}
            onChange={set("notes")}
            size="small"
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSave || saving}
          onClick={() => onSave(form)}
          sx={{ bgcolor: NAVY }}
        >
          {saving ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────
export const SheqSOP: React.FC = () => {
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterBlock, setFilterBlock] = useState<string>("");
  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<SheqSop>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const {
    data: sops = [],
    isLoading,
    error,
  } = useGetSheqSopsQuery({
    category: filterCategory || undefined,
    status: filterStatus || undefined,
    blockId: filterBlock || undefined,
  });

  // Separate unfiltered query for accurate stats counts
  const { data: allSops = [] } = useGetSheqSopsQuery({});

  const { data: blocks = [] } = useGetComsBlocksQuery();

  const [createSop, { isLoading: creating }] = useCreateSheqSopMutation();
  const [updateSop, { isLoading: updating }] = useUpdateSheqSopMutation();
  const [deleteSop] = useDeleteSheqSopMutation();

  const saving = creating || updating;

  const filtered = useMemo(() => {
    if (!search.trim()) return sops;
    const q = search.toLowerCase();
    return sops.filter(
      (s) =>
        s.sopNumber.toLowerCase().includes(q) ||
        s.title.toLowerCase().includes(q) ||
        (s.purpose ?? "").toLowerCase().includes(q) ||
        (s.procedure ?? "").toLowerCase().includes(q),
    );
  }, [sops, search]);

  const handleSave = async (
    data: Omit<SheqSop, "id" | "createdAt" | "updatedAt">,
  ) => {
    if (editing.id) {
      await updateSop({ id: editing.id, body: data });
    } else {
      await createSop(data);
    }
    setDialogOpen(false);
  };

  const handleDelete = async (id: string) => {
    await deleteSop(id);
    setDeleteConfirm(null);
  };

  // Stats computed from ALL sops (unfiltered), not from the current filter view
  const stats = useMemo(() => {
    return {
      total: allSops.length,
      active: allSops.filter((s) => s.status === "ACTIVE").length,
      draft: allSops.filter((s) => s.status === "DRAFT").length,
      reviewDue: allSops.filter(
        (s) =>
          s.status === "ACTIVE" &&
          s.reviewDate &&
          new Date(`${s.reviewDate}T00:00:00`) <= new Date(),
      ).length,
    };
  }, [allSops]);

  return (
    <Box>
      {/* Stats row */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        {[
          { label: "Total SOPs", value: stats.total, color: NAVY },
          { label: "Active", value: stats.active, color: "#2e7d32" },
          { label: "Drafts", value: stats.draft, color: "#9e9e9e" },
          {
            label: "Review Overdue",
            value: stats.reviewDue,
            color: stats.reviewDue > 0 ? "#c62828" : "#9e9e9e",
          },
        ].map((s) => (
          <Card
            key={s.label}
            variant="outlined"
            sx={{ p: 2, minWidth: 130, flex: "1 1 130px" }}
          >
            <Typography variant="h4" fontWeight={700} sx={{ color: s.color }}>
              {s.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {s.label}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* Toolbar */}
      <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
        {/* Search */}
        <Box display="flex" alignItems="center" gap={1} flex="1 1 200px">
          <Search sx={{ color: "text.secondary", fontSize: 20 }} />
          <TextField
            placeholder="Search SOPs…"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
          />
        </Box>

        {/* Filters */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value as string)}
          >
            <MenuItem value="">All categories</MenuItem>
            {SOP_CATEGORIES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as string)}
          >
            <MenuItem value="">All statuses</MenuItem>
            {SOP_STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Site</InputLabel>
          <Select
            label="Site"
            value={filterBlock}
            onChange={(e) => setFilterBlock(e.target.value as string)}
          >
            <MenuItem value="">All sites</MenuItem>
            {blocks.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ bgcolor: NAVY, ml: "auto" }}
          onClick={() => {
            setEditing({});
            setDialogOpen(true);
          }}
        >
          New SOP
        </Button>
      </Box>

      {/* Content */}
      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load SOPs.
        </Alert>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          py={8}
          gap={1}
        >
          <Article sx={{ fontSize: 48, color: "text.disabled" }} />
          <Typography color="text.secondary">
            {sops.length === 0
              ? "No SOPs captured yet."
              : "No SOPs match the current filters."}
          </Typography>
          {sops.length === 0 && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              sx={{ mt: 1 }}
              onClick={() => {
                setEditing({});
                setDialogOpen(true);
              }}
            >
              Add first SOP
            </Button>
          )}
        </Box>
      )}

      {filtered.map((sop) => (
        <SopCard
          key={sop.id}
          sop={sop}
          onEdit={(s) => {
            setEditing(s);
            setDialogOpen(true);
          }}
          onDelete={(id) => setDeleteConfirm(id)}
        />
      ))}

      {/* Create / Edit dialog */}
      <SopFormDialog
        open={dialogOpen}
        initial={editing}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        saving={saving}
        blocks={blocks.map((b) => ({ id: b.id!, name: b.name }))}
      />

      {/* Delete confirm dialog */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete SOP?</DialogTitle>
        <DialogContent>
          <Typography>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
